import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MdSlideshow } from 'react-icons/md';
import { usePhotosInfinite } from '../api';
import { useQueryClient } from '@tanstack/react-query';
import { usePhotoSelection } from './PhotoGallery/hooks/usePhotoSelection';
import { usePhotoDownload } from './PhotoGallery/hooks/usePhotoDownload';
import { getThumbnailUrl } from './PhotoGallery/utils/photoHelpers';
import { useMemoryManagement } from './PhotoGallery/hooks/useMemoryManagement';
import PhotoGrid from './PhotoGallery/PhotoGrid';
import PhotoModal from './PhotoGallery/PhotoModal';
import SlideshowModal from './PhotoGallery/SlideshowModal';
import './PhotoGallery.css';

const PhotoGallery = ({ selectionMode, setSelectionMode, refreshTrigger }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [modalAutoPlay, setModalAutoPlay] = useState(false);
  const [gridConfig, setGridConfig] = useState({ columnCount: 3, itemSize: 120 });
  const [slideshowOpen, setSlideshowOpen] = useState(false);
  const [slideshowStartIndex, setSlideshowStartIndex] = useState(0);
  const slideshowAutoHideDelay = 3000; // 3 seconds default
  // Reduced for mobile optimization - fewer images in memory at once
  const photosPerPage = 20;

  // Query client for cache management
  const queryClient = useQueryClient();

  // Memory management for the entire gallery - optimized for 20 photos per page
  const { updateVisibleImages, cleanupOffscreenImages } = useMemoryManagement({
    bufferMultiplier: 2, // Moderate buffer for window scrolling
    cleanupInterval: 30000 // Cleanup every 30 seconds (more frequent with fewer photos)
  });

  // Use React Query infinite query for photos data
  const {
    data: infiniteData,
    isLoading: photosLoading,
    error: photosError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchPhotos
  } = usePhotosInfinite(photosPerPage);

  // Flatten the infinite data into a single array for easier handling
  const allPhotos = React.useMemo(() =>
    infiniteData?.pages?.flatMap(page => page.photos) || [],
    [infiniteData?.pages]
  );
  const photos = allPhotos; // For backward compatibility with existing code

  // Get total photos count from the first page's pagination data
  const totalPhotos = infiniteData?.pages?.[0]?.pagination?.total || 0;

  // Debug logging
  console.log('PhotoGallery render:', {
    photosLoading,
    photosError,
    allPhotos,
    photos,
    infiniteData,
    hasNextPage,
    isFetchingNextPage
  });


  // Use photo selection hook
  const {
    selectedPhotos,
    selectAll,
    isDraggingSelection,
    lastSelectedPhoto,
    clearSelections,
    togglePhotoSelection,
    toggleSelectAll,
    bindSelectionGesture
  } = usePhotoSelection(selectionMode, photos);

  // Use photo download hook
  const { downloadSelectedPhotos } = usePhotoDownload(selectedPhotos, () => {
    // Callback when download is complete
    setSelectionMode(false);
    clearSelections();
  });

  // Calculate grid configuration based on screen size
  const calculateGridConfig = useCallback(() => {
    const width = window.innerWidth;
    let columnCount = 3;
    let itemSize = 120;

    if (width >= 1024) {
      columnCount = 5;
      itemSize = Math.floor((width - 32) / columnCount); // Account for padding
    } else if (width >= 768) {
      columnCount = 4;
      itemSize = Math.floor((width - 24) / columnCount);
    } else if (width >= 640) {
      columnCount = 4;
      itemSize = Math.floor((width - 16) / columnCount);
    } else {
      columnCount = 3;
      itemSize = Math.floor((width - 16) / columnCount);
    }

    // Ensure minimum item size
    itemSize = Math.max(itemSize, 100);

    setGridConfig({
      columnCount,
      itemSize,
    });
  }, []);

  // Cache photos immediately when loaded from API (tightly coupled with API requests)
  const cachePhotosImmediately = useCallback((newPhotos) => {
    if (!newPhotos || newPhotos.length === 0) return;

    // Process photos in small batches to avoid overwhelming the device
    const batchSize = 5;
    for (let i = 0; i < newPhotos.length; i += batchSize) {
      const batch = newPhotos.slice(i, i + batchSize);

      // Slight delay between batches to prevent blocking
      setTimeout(() => {
        batch.forEach((filename) => {
          // Cache the thumbnail (what's shown in gallery)
          const thumbUrl = getThumbnailUrl(filename);

          // Send direct message to Service Worker to cache immediately
          if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            // Debug logging in development
            if (process.env.NODE_ENV === 'development') {
              console.log('📤 Sending cache request for:', thumbUrl);
            }

            navigator.serviceWorker.controller.postMessage({
              type: 'CACHE_PHOTO_IMMEDIATELY',
              url: thumbUrl
            });
          } else if (process.env.NODE_ENV === 'development') {
            console.log('⚠️ Service Worker not available for caching');
          }
        });
      }, (i / batchSize) * 200); // 200ms delay between batches
    }
  }, []);

  // Track previously loaded photos to detect new ones from API
  const prevPhotosRef = useRef([]);

  useEffect(() => {
    const prevPhotos = prevPhotosRef.current;
    const newPhotos = allPhotos.filter(photo => !prevPhotos.includes(photo));

    if (newPhotos.length > 0) {
      // Small delay to ensure Service Worker is ready, then cache photos immediately when loaded from API
      setTimeout(() => {
        cachePhotosImmediately(newPhotos);
      }, 100);
    }

    prevPhotosRef.current = allPhotos;
  }, [allPhotos, cachePhotosImmediately]);


  // Handle closing modal
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setCurrentPhotoIndex(0);
    setModalAutoPlay(false); // Reset autoPlay state
  }, []);

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!modalOpen) return;

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          handleCloseModal();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, handleCloseModal]);



  // Calculate grid configuration on mount and window resize
  useEffect(() => {
    calculateGridConfig();
    const handleResize = () => calculateGridConfig();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateGridConfig]);

  // Periodic refresh for new photos (useful when multiple users are uploading)
  useEffect(() => {
    const interval = setInterval(() => {
      // Only refresh if the component is visible and not currently fetching
      if (document.visibilityState === 'visible' && !isFetchingNextPage) {
        console.log('🔄 Periodic check for new photos...');
        queryClient.invalidateQueries({
          queryKey: ['photos'],
          refetchType: 'none' // Don't refetch immediately, just mark as stale
        });
        // React Query will refetch when needed due to staleTime
      }
    }, 2 * 60 * 1000); // Check every 2 minutes

    return () => clearInterval(interval);
  }, [queryClient, isFetchingNextPage]);


  // Load more photos function using infinite query
  const loadMorePhotos = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    console.log('Loading next page of photos...');
    fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);


  // Handle authentication errors
  useEffect(() => {
    if (photosError?.response?.status === 403) {
      // Clear invalid token and redirect
      localStorage.removeItem('weddingToken');
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    }
  }, [photosError]);

  // Scroll event listener for infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!hasNextPage || isFetchingNextPage) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Check if there's actually content to scroll to
      const hasScrollableContent = documentHeight > windowHeight;

      if (hasScrollableContent) {
        // Load more when user is within 200px of the bottom
        if (scrollTop + windowHeight >= documentHeight - 200) {
          console.log('Scroll detected near bottom, loading more photos...');
          loadMorePhotos();
        }
      } else {
        // If all content fits in viewport, load more immediately
        console.log('All content fits in viewport, loading more photos...');
        loadMorePhotos();
      }

      // Update memory management for visible images
      updateVisibleImages();

      // Occasionally run cleanup to manage memory
      if (Math.random() < 0.1) {
        cleanupOffscreenImages();
      }
    };

    // Also check on initial load and window resize
    const checkAndLoad = () => {
      if (!hasNextPage || isFetchingNextPage) return;

      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // If all content fits in viewport and we have more photos, load them
      if (documentHeight <= windowHeight && hasNextPage) {
        console.log('Content fits in viewport, loading more photos...');
        loadMorePhotos();
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkAndLoad);

    // Check on initial load
    setTimeout(checkAndLoad, 100);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkAndLoad);
    };
  }, [hasNextPage, isFetchingNextPage, loadMorePhotos, updateVisibleImages, cleanupOffscreenImages]);




  // Service Worker handles photo caching automatically during normal viewing

  // Check cached photos for debugging (reduced logging for performance)
  const checkCachedPhotos = useCallback(async () => {
    if ('caches' in window && process.env.NODE_ENV === 'development') {
      try {
        const cache = await caches.open('wedding-photos-cache');
        const cachedRequests = await cache.keys();
        console.log(`📱 ${cachedRequests.length} photos cached for offline viewing`);
      } catch (error) {
        // Silent fail in production to avoid performance impact
      }
    }
  }, []);

  // Check cached photos when photos load
  useEffect(() => {
    if (allPhotos.length > 0) {
      checkCachedPhotos();
    }
  }, [allPhotos, checkCachedPhotos]);

  // Handle refresh trigger from upload completion
  useEffect(() => {
    if (refreshTrigger > 0) {
      console.log('Refresh trigger activated, refetching photos');
      refetchPhotos();
    }
  }, [refreshTrigger, refetchPhotos]);

  // Handle photo click (selection or viewing)
  const handlePhotoClick = (filename) => {
    if (selectionMode) {
      togglePhotoSelection(filename);
    } else {
      handleOpenModal(filename);
    }
  };

  // Handle opening modal
  const handleOpenModal = (filename) => {
    const photoIndex = allPhotos.findIndex(photo => photo === filename);
    if (photoIndex !== -1) {
      setCurrentPhotoIndex(photoIndex);
      setModalOpen(true);
    }
  };


  // Handle modal slide changes
  const handleModalSlideChange = (newIndex) => {
    setCurrentPhotoIndex(newIndex);

    // Load more photos if we're near the end
    if (newIndex >= photos.length - 5 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Skeleton loading component
  const SkeletonLoader = () => {
    const skeletonItems = Array.from({ length: 12 }, (_, i) => i);

    return (
      <div className="w-full bg-gradient-to-b from-wedding-sage-hint/20 to-wedding-sage-hint/10">
        <div className="photo-grid"
             style={{
               display: 'grid',
               gridTemplateColumns: `repeat(${gridConfig.columnCount}, 1fr)`,
               gap: '0.5rem',
               padding: '0.5rem'
             }}>
          {skeletonItems.map((index) => (
            <div key={index} className="aspect-square overflow-hidden rounded-lg bg-white shadow-sm">
              <div className="w-full h-full bg-gradient-to-r from-wedding-sage/10 via-wedding-sage/20 to-wedding-sage/10 animate-pulse"></div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center space-x-2 py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wedding-sage"></div>
          <span className="text-wedding-dark-brown font-medium">Loading your memories...</span>
        </div>
      </div>
    );
  };

  if (photosLoading) return <SkeletonLoader />;

  if (photosError) {
    const isAuthError = photosError?.response?.status === 403;
    const isNetworkError = !photosError?.response || photosError?.response?.status >= 500;

    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="text-center max-w-md">
          <div className="mb-6">
            {isAuthError ? (
              <svg className="mx-auto h-16 w-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
              </svg>
            ) : isNetworkError ? (
              <svg className="mx-auto h-16 w-16 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-12.728 12.728m0 0L12 12m-6.364 6.364L12 12m6.364-6.364L12 12" />
              </svg>
            ) : (
              <svg className="mx-auto h-16 w-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
              </svg>
            )}
          </div>

          <h2 className="text-xl font-semibold text-wedding-dark-brown mb-3">
            {isAuthError ? 'Authentication Required' : isNetworkError ? 'Connection Issue' : 'Something went wrong'}
          </h2>

          <p className="text-wedding-dark-brown/70 mb-6 leading-relaxed">
            {photosError?.message || 'Failed to load photos'}
          </p>

          <div className="space-y-3">
            <button
              onClick={() => refetchPhotos()}
              className="w-full inline-flex items-center justify-center space-x-2 px-6 py-3 bg-wedding-sage text-white rounded-lg hover:bg-wedding-sage/90 transition-colors shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Try Again</span>
            </button>

            {isAuthError && (
              <button
                onClick={() => window.location.href = '/'}
                className="w-full inline-flex items-center justify-center space-x-2 px-6 py-3 bg-white text-wedding-sage border-2 border-wedding-sage rounded-lg hover:bg-wedding-sage/5 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                <span>Sign In Again</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Empty state when no photos are available
  if (!photosLoading && allPhotos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="text-center max-w-md">
          <svg className="mx-auto h-24 w-24 text-wedding-sage/40 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h2 className="text-xl font-semibold text-wedding-dark-brown mb-2">
            No Photos Yet
          </h2>
          <p className="text-wedding-dark-brown/60 mb-6 leading-relaxed">
            The wedding gallery is empty. Photos will appear here once they're uploaded.
          </p>
          <button
            onClick={() => window.location.href = '/dashboard/upload'}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-wedding-sage text-white rounded-lg hover:bg-wedding-sage/90 transition-colors shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span>Upload First Photos</span>
          </button>
        </div>
      </div>
    );
  }

  try {
    return (
      <div className="w-full bg-gradient-to-b from-wedding-sage-hint/20 to-wedding-sage-hint/10">

      {/* Slideshow Button - Top Right Corner */}
      {!selectionMode && (
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={() => {
              setSlideshowStartIndex(0);
              setSlideshowOpen(true);
            }}
            className="flex items-center justify-center bg-white/90 backdrop-blur-sm p-2 rounded-full border border-wedding-sage/40 shadow-sm transition-all duration-200 hover:bg-white hover:scale-105 active:scale-95 cursor-pointer"
            title="Start slideshow"
            aria-label="Start automatic slideshow"
          >
            <MdSlideshow className="w-8 h-4 text-wedding-sage" />
          </button>
        </div>
      )}

      {/* Floating Action Button - Select */}
      {!selectionMode && (
        <div className="fixed bottom-6 right-4 z-20">
          <div className="relative group">
            <button
              onClick={() => setSelectionMode(true)}
              className="mobile-select-button w-14 h-14 rounded-full shadow-lg bg-wedding-sage text-white hover:bg-wedding-sage/90 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-wedding-sage/50 focus:ring-offset-2 flex items-center justify-center"
              aria-pressed={false}
              aria-label="Enter selection mode to choose multiple photos for download"
              title="Select multiple photos to download or share"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>

            {/* Tooltip */}
            <div className="absolute bottom-full right-0 mb-3 px-3 py-2 bg-wedding-dark-brown text-white text-xs rounded-lg shadow-lg opacity-0 group-active:opacity-100 sm:group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none max-w-[200px] sm:max-w-none">
              Select photos
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-wedding-dark-brown"></div>
            </div>
          </div>
        </div>
      )}
        {/* Selection Mode Controls */}
        {selectionMode && (
          <div className="sticky top-0 z-40 bg-gradient-to-r from-wedding-sage to-wedding-sage/90 border-b-4 border-wedding-sage px-3 sm:px-4 py-4 sm:py-4 mb-4 shadow-lg" role="region" aria-label="Photo selection controls">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              {/* Header with selection info */}
              <div className="flex items-center justify-between sm:justify-start space-x-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className="text-white font-semibold text-base">Selection Mode</h2>
                </div>
                <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  <span className="text-white font-medium text-sm" aria-live="polite" aria-atomic="true">
                    {selectedPhotos.size} of {photos.length} selected
                  </span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center justify-between sm:justify-end space-x-3">
                <button
                  onClick={toggleSelectAll}
                  className={`flex items-center justify-center space-x-2 px-4 py-3 w-32 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-wedding-sage min-h-[44px] ${
                    selectAll
                      ? 'bg-wedding-dark-brown text-white border border-wedding-dark-brown/30 hover:bg-wedding-dark-brown/90 focus:ring-wedding-dark-brown/50'
                      : 'bg-white/20 backdrop-blur-sm text-white border border-white/30 hover:bg-white/30 focus:ring-white/50'
                  }`}
                  aria-label={selectAll ? `Deselect all ${photos.length} photos` : `Select all ${photos.length} photos`}
                >
                  <span className="font-medium text-sm">{selectAll ? 'Deselect All' : 'Select All'}</span>
                </button>

                <button
                  onClick={downloadSelectedPhotos}
                  disabled={selectedPhotos.size === 0}
                  className="flex items-center space-x-2 px-4 py-3 bg-white text-wedding-sage rounded-lg hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-wedding-sage min-h-[44px] flex-1 sm:flex-none"
                  aria-label={`Download ${selectedPhotos.size} selected photos`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                  </svg>
                  <span className="text-sm">Save ({selectedPhotos.size})</span>
                </button>

                <button
                  onClick={() => {
                    setSelectionMode(false);
                    clearSelections();
                  }}
                  className="px-4 py-3 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded-lg hover:bg-white/30 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-wedding-sage min-h-[44px]"
                  aria-label="Exit selection mode"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Photo Grid */}
      <PhotoGrid
        photos={photos}
        selectionMode={selectionMode}
        selectedPhotos={selectedPhotos}
        isDraggingSelection={isDraggingSelection}
        lastSelectedPhoto={lastSelectedPhoto}
        onPhotoClick={handlePhotoClick}
        bindSelectionGesture={bindSelectionGesture}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
      


      {/* Photo Modal */}
      {modalOpen && (
        <PhotoModal
          isOpen={modalOpen}
          photos={allPhotos}
          currentIndex={currentPhotoIndex}
          onClose={handleCloseModal}
          onSlideChange={handleModalSlideChange}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          autoPlay={modalAutoPlay}
          totalPhotos={totalPhotos}
        />
      )}

      {/* Slideshow Modal */}
      {slideshowOpen && (
        <SlideshowModal
          isOpen={slideshowOpen}
          photos={allPhotos}
          startIndex={slideshowStartIndex}
          onClose={() => setSlideshowOpen(false)}
          autoHideDelay={slideshowAutoHideDelay}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
        />
      )}
    </div>
  );
  } catch (error) {
    console.error('Error in PhotoGallery render:', error);
    return (
      <div className="w-full bg-gradient-to-b from-wedding-sage-hint/20 to-wedding-sage-hint/10">
        <div className="flex items-center justify-center py-16 px-4">
          <div className="text-center max-w-md">
            <div className="text-red-500 mb-4">Error loading photo gallery</div>
            <div className="text-sm text-gray-600">
              Please refresh the page to try again.
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default PhotoGallery;

